import {
  createBlankScene,
  createOpening,
  createWall,
  makeId,
  normalizeScene,
  type CameraState,
  type CatalogItem,
  type EditorMode,
  type Measurement,
  type Opening,
  type ProjectSummary,
  type SceneDocument,
  type SceneElement,
  type SceneLayer,
  type SceneOperation,
  type ToolId,
  type Vec2,
  type Vec3,
  type Wall,
} from "@calcplan/domain";
import { FormulaError, resizeElement } from "@calcplan/catalog";
import { paint, paintReach, type PaintTarget } from "@calcplan/cutlist";
import { detectRooms } from "@calcplan/geometry";
import { create } from "zustand";
import { api, ApiError } from "../lib/api";
import {
  actionOverrideSlot,
  attributeOverrideSlot,
  readDynamicPartBounds,
  readLoadedDynamicGraph,
  resolveDynamicAchievableDimensions,
} from "./dynamic-parts";
import {
  canPaintMaterialTarget,
  declaredKindsOf,
  replacePaintAssignment,
} from "./part-paint";
import {
  moveWallVertexInScene,
  type WallVertexReference,
} from "./wall-vertex";

/** Elements inserted in this browser session whose imported resting pose is pending. */
const freshCatalogElementIds = new Set<string>();

export function takeFreshCatalogElement(elementId: string): boolean {
  if (!freshCatalogElementIds.has(elementId)) return false;
  freshCatalogElementIds.delete(elementId);
  return true;
}

export type SaveState =
  | "idle"
  | "loading"
  | "dirty"
  | "saving"
  | "saved"
  | "offline"
  | "error"
  | "conflict";

interface HistoryEntry {
  before: SceneDocument;
  after: SceneDocument;
  command: string;
  commandId: string;
  createdAt: number;
}

interface ApplyOptions {
  coalesceKey?: string;
  recomputeRooms?: boolean;
}

/**
 * A material that came in with an imported module, as the catalogue records it.
 * Shaped by the SKP import; kept structural here so the store does not depend
 * on the API's types.
 */
export interface ImportedMaterial {
  key: string;
  name: string;
  colorHex: string;
  opacity: number;
  colorized: boolean;
  texture?: {
    assetId: string;
    fileName: string;
    mimeType: string;
    widthMm: number;
    heightMm: number;
  };
}

/**
 * Um material que a paleta sabe aplicar, venha ele do catálogo ou do módulo
 * importado. A pintura por peça trata os dois do mesmo jeito; o que muda é só
 * de onde saem a cor e a textura.
 */
export type PaintableMaterial =
  | { source: "catalog"; item: CatalogItem }
  | { source: "imported"; material: ImportedMaterial };

interface EditorState {
  project: ProjectSummary | null;
  scene: SceneDocument;
  confirmedScene: SceneDocument;
  revision: number;
  projectId: string | null;
  mode: EditorMode;
  tool: ToolId;
  selectedIds: string[];
  selectedId: string | null;
  saveState: SaveState;
  lastSavedAt: string | null;
  error: string | null;
  history: HistoryEntry[];
  future: HistoryEntry[];
  generation: number;
  draggedCatalogItemId: string | null;
  cameraByMode: Record<EditorMode, CameraState>;
  load(projectId: string, cacheScope?: string): Promise<void>;
  setMode(mode: EditorMode): void;
  setTool(tool: ToolId): void;
  setDraggedCatalogItem(itemId: string | null): void;
  select(id: string | null, additive?: boolean): void;
  /**
   * Onde se está dentro do módulo, como no SketchUp: dois estados, não um.
   *
   * `partContextPath` é o grupo aberto — aquele em que se entrou com dois
   * cliques. `selectedPartPath` é o que está selecionado dentro dele, sempre um
   * filho direto. Um clique troca a seleção entre os filhos do contexto; dois
   * cliques abrem o que foi clicado e ele vira o novo contexto; Escape fecha o
   * contexto e deixa selecionado o grupo de onde se saiu.
   *
   * Nulos querem dizer "o módulo inteiro", que é o estado de sempre.
   */
  partContextPath: string | null;
  selectedPartPath: string | null;
  selectPart(path: string | null): void;
  /** Abre um grupo: ele passa a ser o contexto, e a seleção entra nele. */
  enterPart(context: string, selection: string | null): void;
  /** Fecha o contexto atual e deixa selecionado o grupo de onde se saiu. */
  leavePart(): void;
  /**
   * Muda um atributo do componente dinâmico nesta instância. Valor nulo desfaz
   * a edição e devolve o atributo ao que o arquivo importado dizia.
   *
   * O elemento vem por parâmetro, e não da seleção: a ferramenta Interagir
   * aciona a peça sob o cursor sem selecionar nada, e uma escrita que dependia
   * da seleção era descartada em silêncio — a gaveta não abria.
   */
  setPartAttribute(
    elementId: string,
    path: string,
    attribute: string,
    value: string | number | null,
    /**
     * `action` marca o que veio da ferramenta Interagir. Abrir uma gaveta não é
     * a mesma coisa que mudar a medida dela: a ação só move o desenho, e é
     * gravada à parte para poder ser desfeita sem tocar nas edições.
     */
    kind?: "edit" | "action",
  ): void;
  apply(
    command: string,
    mutate: (draft: SceneDocument) => void,
    options?: ApplyOptions,
  ): void;
  undo(): void;
  redo(): void;
  save(): Promise<void>;
  discardChanges(): void;
  exportRecovery(): void;
  addWall(startMm: Vec2, endMm: Vec2): string;
  updateWall(wallId: string, patch: Partial<Wall>): void;
  moveWallVertex(references: WallVertexReference[], targetMm: Vec2): void;
  addOpening(kind: "door" | "window", wallId: string, offsetMm: number): string | null;
  updateOpening(openingId: string, patch: Partial<Opening>): void;
  addMeasurement(pointsMm: Vec3[], scope?: Measurement["scope"]): string;
  updateMeasurement(measurementId: string, patch: Partial<Measurement>): void;
  addCatalogItem(item: CatalogItem, positionMm?: Vec3): string | null;
  addPrimitive(
    shape: "box" | "cylinder" | "sphere",
    positionMm?: Vec3,
    dimensionsMm?: SceneElement["dimensionsMm"],
  ): string;
  updateSelected(patch: Partial<SceneElement>): void;
  resizeSelected(
    item: CatalogItem | null,
    dimensions: Partial<SceneElement["dimensionsMm"]>,
    translationMm?: Vec3,
  ): void;
  updateSelectedTransform(translationMm: Vec3, rotationRad?: Vec3): void;
  deleteSelection(): void;
  duplicateSelection(): void;
  renameProject(name: string): void;
  paintElements(
    seedIds: string[],
    target: PaintTarget,
    materialId: string,
    reach?: "instance" | "definition",
  ): void;
  /**
   * Põe um material numa peça de dentro do módulo, ou no alvo inteiro.
   *
   * `path` nulo pinta o módulo, a parede ou o piso; um caminho pinta aquele nó
   * do componente. A gravação é sempre a mesma — uma entrada em
   * `materialAssignments` com o caminho por chave —, que é onde a extração já
   * procura a pintura de uma peça, então pintar pela tela e ler na lista de
   * corte não podem discordar.
   */
  paintPart(
    targetIds: string[],
    path: string | null,
    material: PaintableMaterial,
  ): void;
  addDrawnElement(element: SceneElement): void;
  addLayer(name?: string): string;
  updateLayer(layerId: string, patch: Partial<SceneLayer>): void;
  reorderLayer(layerId: string, direction: -1 | 1): void;
  deleteLayer(layerId: string): void;
  addOperation(operation: SceneOperation): void;
  removeOperation(operationId: string): void;
  setCamera(mode: EditorMode, patch: Partial<CameraState>): void;
  reset(cacheScope?: string): void;
}

const defaultCamera: CameraState = {
  projection: "perspective",
  targetMm: [5_000, 1_300, 5_000],
  azimuthRad: -Math.PI / 4,
  elevationRad: Math.PI / 3.2,
  distanceMm: 16_000,
  orthoSizeMm: 12_000,
  fovRad: 0.8,
  nearMm: 10,
  farMm: 100_000,
};

const emptyScene = createBlankScene();
let saveInFlight: Promise<void> | null = null;
let saveRequestedWhileRunning = false;
let activeAgentTurnId: string | null = null;
const EDITOR_SESSION_CACHE_TTL_MS = 60_000;

interface CachedEditorSession {
  storedAt: number;
  project: ProjectSummary;
  scene: SceneDocument;
  confirmedScene: SceneDocument;
  revision: number;
  lastSavedAt: string | null;
  cameraByMode: Record<EditorMode, CameraState>;
}

/**
 * A short, identity-scoped handoff between the editor and dashboard.
 *
 * Leaving a clean project used to discard the entire normalized document, so
 * coming straight back repeated both API reads before the 3D viewport could
 * even begin. One minute is enough for the common "back, check, reopen" flow
 * without masking collaboration updates after the user spends time elsewhere.
 */
const recentEditorSessions = new Map<string, CachedEditorSession>();

function editorSessionKey(cacheScope: string, projectId: string): string {
  return `${cacheScope}:${projectId}`;
}

function readRecentEditorSession(
  cacheScope: string | undefined,
  projectId: string,
): CachedEditorSession | null {
  if (!cacheScope) return null;
  const key = editorSessionKey(cacheScope, projectId);
  const cached = recentEditorSessions.get(key);
  if (!cached) return null;
  if (Date.now() - cached.storedAt > EDITOR_SESSION_CACHE_TTL_MS) {
    recentEditorSessions.delete(key);
    return null;
  }
  return cached;
}

function storeRecentEditorSession(
  cacheScope: string | undefined,
  state: EditorState,
): void {
  if (!cacheScope || !state.projectId || !state.project || state.saveState !== "saved") return;
  const key = editorSessionKey(cacheScope, state.projectId);
  recentEditorSessions.set(key, {
    storedAt: Date.now(),
    project: structuredClone(state.project),
    scene: cloneScene(state.scene),
    confirmedScene: cloneScene(state.confirmedScene),
    revision: state.revision,
    lastSavedAt: state.lastSavedAt,
    cameraByMode: structuredClone(state.cameraByMode),
  });
  while (recentEditorSessions.size > 3) {
    const oldest = recentEditorSessions.keys().next().value as string | undefined;
    if (!oldest) break;
    recentEditorSessions.delete(oldest);
  }
}

/**
 * Runs one synchronous public store action as part of an agent turn.
 *
 * The marker exists only while the action itself executes, so a manual edit in
 * between two tool calls is never accidentally absorbed into the agent's undo
 * entry. Consecutive actions carrying the same turn id coalesce without the
 * normal 500 ms gesture timeout.
 */
export function withAgentTurn<T>(turnId: string, action: () => T): T {
  const previous = activeAgentTurnId;
  activeAgentTurnId = turnId;
  try {
    return action();
  } finally {
    activeAgentTurnId = previous;
  }
}

function cloneScene(scene: SceneDocument): SceneDocument {
  return structuredClone(scene);
}

/** Keeps a computed size readable in the panel: whole tenths of a millimetre. */
function roundDimensions(
  dimensions: SceneElement["dimensionsMm"],
): SceneElement["dimensionsMm"] {
  const round = (value: number) => Math.max(1, Math.round(value * 10) / 10);
  return {
    width: round(dimensions.width),
    height: round(dimensions.height),
    depth: round(dimensions.depth),
  };
}

function recoveryKey(projectId: string): string {
  return `calcplan:recovery:${projectId}`;
}

function persistRecovery(projectId: string | null, scene: SceneDocument): void {
  if (!projectId) return;
  try {
    sessionStorage.setItem(recoveryKey(projectId), JSON.stringify(scene));
  } catch {
    // A full recovery export remains available even when browser storage is full.
  }
}

function clearRecovery(projectId: string | null): void {
  if (projectId) sessionStorage.removeItem(recoveryKey(projectId));
}

function insertElementFromCatalog(item: CatalogItem, positionMm: Vec3): SceneElement {
  const engineering = item.engineering;
  return {
    id: makeId("element"),
    kind: engineering
      ? "model"
      : item.root === "Estruturas"
        ? "panel"
        : item.type === "architecture"
          ? "geometry"
          : "module",
    revision: 0,
    name: item.name,
    layerId: "layer-architecture",
    transform: {
      translationMm: positionMm,
      rotationRad: [0, 0, 0],
      scale: [1, 1, 1],
    },
    dimensionsMm: {
      width: Math.max(10, item.dimensionsMm.width),
      height: Math.max(10, item.dimensionsMm.height),
      depth: Math.max(10, item.dimensionsMm.depth),
    },
    catalogRef: { itemId: item.stableItemId, revision: item.revision ?? "1.0.0" },
    materialId: item.color,
    materialAssignments: {},
    locked: false,
    visible: true,
    parameters: {
      ...(item.parameterDefaults ?? {}),
      resize: item.resize ?? null,
      formulas: item.formulas ?? {},
    },
    componentVisibility: {},
    instanceOverrides: {},
    operationIds: [],
    extensions: engineering
      ? {
          importedAssetId: engineering.renderAssetId,
          importedFileName: `${item.stableItemId}.glb`,
          importedMimeType: "model/gltf-binary",
          normalizedUnit: "m",
          pivot: "bounds-center",
          engineeringSourceAssetId: engineering.sourceAssetId,
          engineeringSourceFileName: engineering.sourceFileName,
          engineeringComponentRoot: engineering.root,
          engineeringMeshBindings: engineering.meshBindings,
          engineeringDynamicAssetId: engineering.dynamicAssetId,
        }
      : {},
  };
}

export const useEditorStore = create<EditorState>((set, get) => ({
  project: null,
  scene: emptyScene,
  confirmedScene: cloneScene(emptyScene),
  revision: 1,
  projectId: null,
  mode: "plan",
  tool: "select",
  selectedIds: [],
  selectedId: null,
  selectedPartPath: null,
  partContextPath: null,
  saveState: "idle",
  lastSavedAt: null,
  error: null,
  history: [],
  future: [],
  generation: 0,
  draggedCatalogItemId: null,
  cameraByMode: {
    plan: { ...defaultCamera, projection: "orthographic" },
    front: { ...defaultCamera, projection: "orthographic" },
    "3d": structuredClone(defaultCamera),
  },

  async load(projectId, cacheScope) {
    const cached = readRecentEditorSession(cacheScope, projectId);
    if (cached) {
      set({
        projectId,
        project: structuredClone(cached.project),
        scene: cloneScene(cached.scene),
        confirmedScene: cloneScene(cached.confirmedScene),
        revision: cached.revision,
        saveState: "saved",
        lastSavedAt: cached.lastSavedAt,
        error: null,
        selectedIds: [],
        selectedId: null,
        selectedPartPath: null,
        partContextPath: null,
        history: [],
        future: [],
        generation: 0,
        cameraByMode: structuredClone(cached.cameraByMode),
      });
      return;
    }
    set({
      projectId,
      saveState: "loading",
      error: null,
      selectedIds: [],
      selectedId: null,
      history: [],
      future: [],
    });
    try {
      const [projectResponse, sceneResponse] = await Promise.all([
        api<{ data: ProjectSummary }>(`/projects/${projectId}`),
        api<{ data: { documentId: string; revision: number; snapshot: unknown; updatedAt: string } }>(
          `/projects/${projectId}/scene`,
        ),
      ]);
      let normalized = normalizeScene(sceneResponse.data.snapshot, sceneResponse.data.documentId);
      if (!normalized.rooms.length && normalized.walls.length >= 3) {
        normalized.rooms = detectRooms(normalized);
      }
      normalized.revision = sceneResponse.data.revision;
      const recovery = sessionStorage.getItem(recoveryKey(projectId));
      let recovered = false;
      if (recovery) {
        try {
          const candidate = normalizeScene(JSON.parse(recovery), sceneResponse.data.documentId);
          if (candidate.metadata.updatedAt > normalized.metadata.updatedAt) {
            normalized = candidate;
            recovered = true;
          }
        } catch {
          clearRecovery(projectId);
        }
      }
      const confirmed = normalizeScene(sceneResponse.data.snapshot, sceneResponse.data.documentId);
      if (!confirmed.rooms.length && confirmed.walls.length >= 3) confirmed.rooms = detectRooms(confirmed);
      set({
        project: projectResponse.data,
        scene: normalized,
        confirmedScene: confirmed,
        revision: sceneResponse.data.revision,
        saveState: recovered ? "dirty" : "saved",
        lastSavedAt: sceneResponse.data.updatedAt,
        generation: 0,
        error: recovered ? "Um rascunho local mais recente foi recuperado." : null,
      });
    } catch (error) {
      set({
        saveState: navigator.onLine ? "error" : "offline",
        error: error instanceof ApiError ? error.message : "Não foi possível abrir o projeto.",
      });
    }
  },

  setMode(mode) {
    set({ mode });
  },
  setTool(tool) {
    set({ tool });
  },
  setDraggedCatalogItem(itemId) {
    set({ draggedCatalogItemId: itemId });
  },
  select(id, additive = false) {
    if (!id) {
      set({ selectedIds: [], selectedId: null, selectedPartPath: null, partContextPath: null });
      return;
    }
    const ids = additive
      ? get().selectedIds.includes(id)
        ? get().selectedIds.filter((candidate) => candidate !== id)
        : [...get().selectedIds, id]
      : [id];
    // Trocar de módulo sai de dentro do anterior: um caminho de peça só faz
    // sentido dentro do módulo em que foi escolhido.
    const leaving = ids.at(-1) !== get().selectedId;
    set({
      selectedIds: ids,
      selectedId: ids.at(-1) ?? null,
      ...(leaving ? { selectedPartPath: null, partContextPath: null } : {}),
    });
  },

  selectPart(path) {
    if (!path) {
      set({ selectedPartPath: null, partContextPath: null });
      return;
    }
    set({ selectedPartPath: path });
  },

  enterPart(context, selection) {
    // Sem seleção, o painel mostra o grupo aberto e nada fica marcado no 3D
    // até que se clique em alguma coisa lá dentro.
    set({ partContextPath: context, selectedPartPath: selection });
  },

  leavePart() {
    const context = get().partContextPath;
    if (!context) {
      set({ selectedPartPath: null, partContextPath: null });
      return;
    }
    const above = context.split(" / ").slice(0, -1).join(" / ");
    // Saindo do contexto, o grupo de onde se saiu fica selecionado — é onde a
    // atenção estava, e é de lá que se continua andando pelos irmãos.
    set({ partContextPath: above || null, selectedPartPath: context });
  },

  setPartAttribute(elementId, path, attribute, value, kind = "edit") {
    if (!elementId) return;
    const slot = kind === "action"
      ? actionOverrideSlot(path, attribute)
      : attributeOverrideSlot(path, attribute);
    get().apply("element.attribute", (draft) => {
      const element = draft.elements.find((candidate) => candidate.id === elementId);
      if (!element) return;
      const overrides = { ...(element.instanceOverrides ?? {}) };
      if (value === null) delete overrides[slot];
      else overrides[slot] = value;
      element.instanceOverrides = overrides;
      element.revision += 1;
    });
  },

  apply(command, mutate, options = {}) {
    const state = get();
    const current = cloneScene(state.scene);
    const next = cloneScene(current);
    mutate(next);
    if (options.recomputeRooms) next.rooms = detectRooms(next);
    next.metadata.updatedAt = new Date().toISOString();
    next.revision = state.revision;
    const createdAt = Date.now();
    const latest = state.history.at(-1);
    const agentCoalesceKey = activeAgentTurnId ? `agent:${activeAgentTurnId}` : null;
    const coalesceKey = agentCoalesceKey ?? options.coalesceKey;
    const canCoalesce = Boolean(
      coalesceKey &&
      latest?.command === coalesceKey &&
      (agentCoalesceKey || createdAt - latest.createdAt <= 500),
    );
    const entry: HistoryEntry = canCoalesce && latest
      ? {
          ...latest,
          after: cloneScene(next),
          createdAt,
        }
      : {
          before: current,
          after: cloneScene(next),
          command: coalesceKey ?? command,
          commandId: makeId("command"),
          createdAt,
        };
    const history = canCoalesce
      ? [...state.history.slice(0, -1), entry]
      : [...state.history.slice(-99), entry];
    persistRecovery(state.projectId, next);
    set({
      scene: next,
      saveState: "dirty",
      history,
      future: [],
      generation: state.generation + 1,
      error: null,
    });
  },

  undo() {
    const state = get();
    const previous = state.history.at(-1);
    if (!previous) return;
    const scene = cloneScene(previous.before);
    persistRecovery(state.projectId, scene);
    set({
      scene,
      history: state.history.slice(0, -1),
      future: [previous, ...state.future].slice(0, 100),
      saveState: "dirty",
      selectedIds: [],
      selectedId: null,
      generation: state.generation + 1,
    });
  },

  redo() {
    const state = get();
    const next = state.future[0];
    if (!next) return;
    const scene = cloneScene(next.after);
    persistRecovery(state.projectId, scene);
    set({
      scene,
      history: [...state.history, next].slice(-100),
      future: state.future.slice(1),
      saveState: "dirty",
      selectedIds: [],
      selectedId: null,
      generation: state.generation + 1,
    });
  },

  async save() {
    if (saveInFlight) {
      saveRequestedWhileRunning = true;
      await saveInFlight;
      return;
    }
    const state = get();
    if (!state.projectId || !["dirty", "error", "offline"].includes(state.saveState)) return;
    const run = async () => {
      const savingState = get();
      const savingGeneration = savingState.generation;
      const snapshot = cloneScene(savingState.scene);
      set({ saveState: "saving", error: null });
      try {
        const response = await api<{
          data: { revision: number; snapshotHash: string; warnings: string[] };
        }>(`/projects/${savingState.projectId}/scene`, {
          method: "PATCH",
          body: {
            schemaVersion: 1,
            expectedRevision: savingState.revision,
            operationId: makeId("op"),
            commandType: savingState.history.at(-1)?.command ?? "scene.snapshot.replace",
            snapshot,
          },
        });
        const changedWhileSaving = get().generation !== savingGeneration;
        snapshot.revision = response.data.revision;
        // Keep the live scene reference intact: a save acknowledgement may arrive
        // while the viewport owns an in-progress pointer transform.
        set({
          revision: response.data.revision,
          confirmedScene: snapshot,
          saveState: changedWhileSaving ? "dirty" : "saved",
          lastSavedAt: new Date().toISOString(),
          error: response.data.warnings[0] ?? null,
        });
        if (!changedWhileSaving) clearRecovery(savingState.projectId);
      } catch (error) {
        const conflict =
          error instanceof ApiError && error.code === "DOCUMENT_REVISION_CONFLICT";
        set({
          saveState: conflict ? "conflict" : navigator.onLine ? "error" : "offline",
          error:
            error instanceof ApiError
              ? error.message
              : error instanceof Error
                ? error.message
              : navigator.onLine
                ? "Não foi possível salvar."
                : "Sem conexão. O rascunho permanece neste dispositivo.",
        });
      }
    };
    saveInFlight = run().finally(() => {
      saveInFlight = null;
    });
    await saveInFlight;
    if (saveRequestedWhileRunning) {
      saveRequestedWhileRunning = false;
      if (get().saveState === "dirty") await get().save();
    }
  },

  discardChanges() {
    const state = get();
    clearRecovery(state.projectId);
    set({
      scene: cloneScene(state.confirmedScene),
      saveState: "saved",
      history: [],
      future: [],
      selectedIds: [],
      selectedId: null,
      error: null,
      generation: state.generation + 1,
    });
  },

  exportRecovery() {
    const state = get();
    const blob = new Blob([JSON.stringify(state.scene, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${state.scene.metadata.name.replace(/[^\p{L}\p{N}-]+/gu, "-")}-recuperacao.calcme3d.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  },

  addWall(startMm, endMm) {
    let wallId = "";
    get().apply(
      "wall.add",
      (scene) => {
        const wall = createWall(
          startMm,
          endMm,
          scene.settings,
          scene.layers.find((layer) => !layer.locked)?.id ?? scene.layers[0]!.id,
        );
        wallId = wall.id;
        scene.walls.push(wall);
      },
      { recomputeRooms: true },
    );
    return wallId;
  },

  updateWall(wallId, patch) {
    get().apply(
      "wall.update",
      (scene) => {
        const wall = scene.walls.find((candidate) => candidate.id === wallId);
        if (!wall || (wall.locked && Object.keys(patch).some((key) => key !== "locked"))) return;
        Object.assign(wall, patch, { revision: wall.revision + 1 });
        for (const opening of scene.openings.filter((candidate) => candidate.wallId === wallId)) {
          opening.depthMm = wall.thicknessMm;
          const available = Math.max(0, Math.hypot(
            wall.endMm[0] - wall.startMm[0],
            wall.endMm[1] - wall.startMm[1],
          ) - opening.widthMm);
          opening.offsetMm = Math.min(opening.offsetMm, available);
        }
      },
      { recomputeRooms: true, coalesceKey: `wall.update:${wallId}` },
    );
  },

  moveWallVertex(references, targetMm) {
    const referenceKey = references
      .map((reference) => `${reference.wallId}:${reference.endpoint}`)
      .sort()
      .join("|");
    get().apply(
      "wall.vertex.move",
      (scene) => {
        moveWallVertexInScene(scene, references, targetMm);
      },
      { recomputeRooms: true, coalesceKey: `wall.vertex.move:${referenceKey}` },
    );
  },

  addOpening(kind, wallId, offsetMm) {
    const wall = get().scene.walls.find((candidate) => candidate.id === wallId);
    if (!wall || wall.locked) return null;
    let openingId = "";
    get().apply("opening.add", (scene) => {
      const target = scene.walls.find((candidate) => candidate.id === wallId);
      if (!target) return;
      const opening = createOpening(kind, target, offsetMm);
      openingId = opening.id;
      scene.openings.push(opening);
      target.openingIds.push(opening.id);
      target.revision += 1;
    });
    set({ selectedIds: [openingId], selectedId: openingId, tool: "select" });
    return openingId;
  },

  updateOpening(openingId, patch) {
    get().apply(
      "opening.update",
      (scene) => {
        const opening = scene.openings.find((candidate) => candidate.id === openingId);
        if (!opening) return;
        Object.assign(opening, patch, { revision: opening.revision + 1 });
        const wall = scene.walls.find((candidate) => candidate.id === opening.wallId);
        if (wall) {
          const available = Math.max(
            0,
            Math.hypot(
              wall.endMm[0] - wall.startMm[0],
              wall.endMm[1] - wall.startMm[1],
            ) - opening.widthMm,
          );
          opening.offsetMm = Math.max(0, Math.min(opening.offsetMm, available));
        }
      },
      { coalesceKey: `opening.update:${openingId}` },
    );
  },

  addMeasurement(pointsMm, scope = "analysis") {
    const id = makeId("measurement");
    get().apply("measurement.add", (scene) => {
      scene.measurements.push({ id, kind: "linear", pointsMm, scope });
    });
    set({ selectedIds: [id], selectedId: id, tool: "select" });
    return id;
  },

  updateMeasurement(measurementId, patch) {
    get().apply(
      "measurement.update",
      (scene) => {
        const measurement = scene.measurements.find(
          (candidate) => candidate.id === measurementId,
        );
        if (measurement) Object.assign(measurement, patch);
      },
      { coalesceKey: `measurement.update:${measurementId}` },
    );
  },

  addCatalogItem(item, positionMm = [5_000, item.dimensionsMm.height / 2, 5_000]) {
    if (item.type === "material") return null;
    let createdId = "";
    get().apply("element.add", (scene) => {
      const element = insertElementFromCatalog(item, positionMm);
      element.layerId = scene.layers.find((layer) => !layer.locked)?.id ?? scene.layers[0]!.id;
      createdId = element.id;
      scene.elements.push(element);
    });
    freshCatalogElementIds.add(createdId);
    set({ selectedIds: [createdId], selectedId: createdId, tool: "select" });
    return createdId;
  },

  addPrimitive(shape, positionMm = [5_000, 500, 5_000], dimensionsMm) {
    const id = makeId("primitive");
    get().apply("element.primitive.add", (scene) => {
      scene.elements.push({
        id,
        kind: "primitive",
        revision: 0,
        name: shape === "box" ? "Caixa" : shape === "cylinder" ? "Cilindro" : "Esfera",
        layerId: scene.layers.find((layer) => !layer.locked)?.id ?? scene.layers[0]!.id,
        transform: { translationMm: positionMm, rotationRad: [0, 0, 0], scale: [1, 1, 1] },
        dimensionsMm: dimensionsMm ?? { width: 1_000, height: 1_000, depth: 1_000 },
        materialId: "#94A3B8",
        materialAssignments: {},
        locked: false,
        visible: true,
        parameters: { primitive: shape },
        componentVisibility: {},
        instanceOverrides: {},
        operationIds: [],
        extensions: {},
      });
    });
    set({ selectedIds: [id], selectedId: id, tool: "select" });
    return id;
  },

  updateSelected(patch) {
    const selectedId = get().selectedId;
    if (!selectedId) return;
    get().apply(
      "element.update",
      (scene) => {
        const element = scene.elements.find((candidate) => candidate.id === selectedId);
        const patchKeys = Object.keys(patch);
        const isUnlockOnly = element?.locked
          && patch.locked === false
          && patchKeys.every((key) => key === "locked");
        if (element && (!element.locked || isUnlockOnly)) {
          Object.assign(element, patch, { revision: element.revision + 1 });
        }
      },
      { coalesceKey: `element.update:${selectedId}` },
    );
  },

  resizeSelected(item, dimensions, translationMm) {
    const selectedId = get().selectedId;
    if (!selectedId) return;
    try {
      get().apply(
        "element.resize",
        (scene) => {
          const index = scene.elements.findIndex((candidate) => candidate.id === selectedId);
          const element = scene.elements[index];
          if (!element || element.locked) return;
          const resizeSource = item
            ? {
                ...element,
                parameters: { ...(item.parameterDefaults ?? {}), ...element.parameters },
              }
            : element;
          const resized = item
            ? resizeElement(resizeSource, item, dimensions)
            : {
                ...element,
                dimensionsMm: { ...element.dimensionsMm, ...dimensions },
                revision: element.revision + 1,
              };
          // A dynamic component decides its own size: it may cap a length, or
          // ignore an axis its formulas never wire up. Committing what was
          // typed instead of what the module reached leaves the element box
          // enclosing empty space.
          const graph = readLoadedDynamicGraph(resized);
          if (graph) {
            resized.dimensionsMm = roundDimensions(
              resolveDynamicAchievableDimensions(
                graph,
                resized.dimensionsMm,
                readDynamicPartBounds(resized),
              ),
            );
          }
          if (translationMm) {
            resized.transform = { ...resized.transform, translationMm };
          }
          scene.elements[index] = resized;
        },
        { coalesceKey: `element.resize:${selectedId}` },
      );
      set({ error: null });
    } catch (error) {
      set({
        error: error instanceof FormulaError
          ? error.message
          : "Não foi possível recalcular o módulo.",
      });
    }
  },

  updateSelectedTransform(translationMm, rotationRad) {
    const selectedId = get().selectedId;
    const element = get().scene.elements.find((candidate) => candidate.id === selectedId);
    if (!element) return;
    get().updateSelected({
      transform: {
        ...element.transform,
        translationMm,
        rotationRad: rotationRad ?? element.transform.rotationRad,
      },
    });
  },

  deleteSelection() {
    const ids = new Set(get().selectedIds);
    if (!ids.size) return;
    get().apply(
      "selection.delete",
      (scene) => {
        const removedOpenings = new Set(
          scene.openings
            .filter((opening) => ids.has(opening.id) || ids.has(opening.wallId))
            .map((opening) => opening.id),
        );
        scene.openings = scene.openings.filter(
          (opening) => !ids.has(opening.id) && !ids.has(opening.wallId),
        );
        scene.walls = scene.walls
          .filter((wall) => !ids.has(wall.id) && !wall.locked)
          .map((wall) => ({
            ...wall,
            openingIds: wall.openingIds.filter((id) => !removedOpenings.has(id)),
          }));
        scene.elements = scene.elements.filter(
          (element) => !ids.has(element.id) || element.locked,
        );
        scene.measurements = scene.measurements.filter((measurement) => !ids.has(measurement.id));
        scene.operations = scene.operations.filter(
          (operation) =>
            !ids.has(operation.id) &&
            !("targetElementId" in operation && ids.has(operation.targetElementId)),
        );
      },
      { recomputeRooms: true },
    );
    set({ selectedIds: [], selectedId: null });
  },

  duplicateSelection() {
    const state = get();
    const selectedElements = state.scene.elements.filter((element) =>
      state.selectedIds.includes(element.id),
    );
    if (!selectedElements.length) return;
    const ids: string[] = [];
    get().apply("selection.duplicate", (scene) => {
      for (const source of selectedElements) {
        const duplicate = structuredClone(source);
        duplicate.id = makeId("element");
        duplicate.revision = 0;
        duplicate.name = `${source.name} cópia`;
        duplicate.transform.translationMm = [
          source.transform.translationMm[0] + 200,
          source.transform.translationMm[1],
          source.transform.translationMm[2] + 200,
        ];
        duplicate.operationIds = [];
        ids.push(duplicate.id);
        scene.elements.push(duplicate);
      }
    });
    set({ selectedIds: ids, selectedId: ids.at(-1) ?? null });
  },

  renameProject(name) {
    if (!name.trim()) return;
    get().apply(
      "project.name.update",
      (scene) => {
        scene.metadata.name = name.trim();
      },
      { coalesceKey: "project.name.update" },
    );
    set((state) => ({
      project: state.project ? { ...state.project, name: name.trim() } : state.project,
    }));
  },

  /**
   * The paint tool (14.4): a material on the part, on one of its edges, or on
   * one of its faces, reaching either this instance or every copy of it.
   *
   * The rules live in `@calcplan/cutlist` as pure functions over the element
   * list — the same ones the Estrutura tab runs — so painting from the viewport
   * and painting from the tree can never disagree.
   */
  paintElements(seedIds, target, materialId, reach = "instance") {
    if (!seedIds.length) return;
    const ids = paintReach(get().scene.elements, seedIds, reach);
    if (!ids.length) return;
    get().apply("material.paint", (scene) => {
      const result = paint(scene.elements, ids, target, materialId);
      if (!result.changed.length) return;
      scene.elements = result.elements;
    });
  },

  paintPart(targetIds, path, material) {
    if (!targetIds.length) return;
    const definition = materialDefinitionOf(material);
    get().apply("material.paint", (scene) => {
      if (!scene.materials.some((candidate) => candidate.id === definition.id)) {
        scene.materials.push(definition);
      }
      const assignment = { materialId: definition.id, revision: definition.revision };
      for (const targetId of targetIds) {
        const element = scene.elements.find((candidate) => candidate.id === targetId);
        if (element && !element.locked) {
          const graph = path ? readLoadedDynamicGraph(element) : null;
          if (path && graph && !canPaintMaterialTarget(path, declaredKindsOf(graph))) continue;
          element.materialAssignments = replacePaintAssignment(
            element.materialAssignments,
            path,
            assignment,
          );
          /*
           * Pintar o módulo inteiro é a única pintura que também troca a cor
           * plana dele: é ela que desenha um móvel sem componente dinâmico e é
           * ela que a lista de corte herda quando a peça não diz nada. Pintar
           * uma peça de dentro não pode mexer nisso, senão a primeira peça
           * pintada arrastaria o módulo inteiro junto.
           */
          if (!path) element.materialId = definition.id;
          element.revision += 1;
          continue;
        }
        /*
         * Parede e piso também recebem material da mesma paleta, e não têm peça
         * dentro: para eles só existe a pintura inteira. Deixá-los de fora daqui
         * fazia a amostra funcionar no móvel e não fazer nada na parede.
         */
        const wall = scene.walls.find((candidate) => candidate.id === targetId);
        if (wall) {
          wall.materialId = definition.id;
          wall.materialAssignments.default = assignment;
          wall.revision += 1;
          continue;
        }
        const room = scene.rooms.find((candidate) => candidate.id === targetId);
        if (room) {
          room.materialAssignments.default = assignment;
          continue;
        }
        const opening = scene.openings.find((candidate) => candidate.id === targetId);
        if (opening) {
          opening.materialId = definition.id;
          opening.materialAssignments.default = assignment;
          opening.revision += 1;
        }
      }
    });
  },

  /**
   * Puts a part the drawing tool produced into the scene, and selects it.
   *
   * Selecting it is the point: a part you just drew is the one you are about to
   * name, resize or paint, and having to hunt for it undoes the drawing.
   */
  addDrawnElement(element) {
    get().apply("element.draw", (scene) => {
      scene.elements.push(element);
    });
    get().select(element.id, false);
  },

  addLayer(name) {
    const id = makeId("layer");
    get().apply("layer.add", (scene) => {
      scene.layers.push({
        id,
        name: name?.trim() || `Camada ${scene.layers.length + 1}`,
        order: scene.layers.length,
        visible: true,
        locked: false,
        opacity: 1,
      });
    });
    return id;
  },

  updateLayer(layerId, patch) {
    get().apply(
      "layer.update",
      (scene) => {
        const layer = scene.layers.find((candidate) => candidate.id === layerId);
        if (layer) Object.assign(layer, patch);
      },
      { coalesceKey: `layer.update:${layerId}` },
    );
  },

  reorderLayer(layerId, direction) {
    get().apply("layer.reorder", (scene) => {
      const sorted = [...scene.layers].sort((a, b) => a.order - b.order);
      const index = sorted.findIndex((layer) => layer.id === layerId);
      const targetIndex = index + direction;
      if (index < 0 || targetIndex < 0 || targetIndex >= sorted.length) return;
      [sorted[index], sorted[targetIndex]] = [sorted[targetIndex]!, sorted[index]!];
      sorted.forEach((layer, order) => {
        layer.order = order;
      });
      scene.layers = sorted;
    });
  },

  deleteLayer(layerId) {
    get().apply("layer.delete", (scene) => {
      if (scene.layers.length <= 1) return;
      const layer = scene.layers.find((candidate) => candidate.id === layerId);
      const fallback = scene.layers.find((candidate) => candidate.id !== layerId);
      if (!layer || !fallback) return;
      for (const wall of scene.walls.filter((candidate) => candidate.layerId === layerId)) {
        wall.layerId = fallback.id;
      }
      for (const element of scene.elements.filter((candidate) => candidate.layerId === layerId)) {
        element.layerId = fallback.id;
      }
      for (const room of scene.rooms.filter((candidate) => candidate.layerId === layerId)) {
        room.layerId = fallback.id;
      }
      scene.layers = scene.layers
        .filter((candidate) => candidate.id !== layerId)
        .map((candidate, order) => ({ ...candidate, order }));
    });
  },

  addOperation(operation) {
    get().apply("operation.add", (scene) => {
      scene.operations.push(operation);
      if ("targetElementId" in operation) {
        const element = scene.elements.find(
          (candidate) => candidate.id === operation.targetElementId,
        );
        if (element && !element.operationIds.includes(operation.id)) {
          element.operationIds.push(operation.id);
          element.revision += 1;
        }
      }
    });
  },

  removeOperation(operationId) {
    get().apply("operation.delete", (scene) => {
      scene.operations = scene.operations.filter((operation) => operation.id !== operationId);
      for (const element of scene.elements) {
        element.operationIds = element.operationIds.filter((id) => id !== operationId);
      }
    });
  },

  setCamera(mode, patch) {
    set((state) => ({
      cameraByMode: {
        ...state.cameraByMode,
        [mode]: { ...state.cameraByMode[mode], ...patch },
      },
    }));
  },

  reset(cacheScope) {
    storeRecentEditorSession(cacheScope, get());
    const scene = createBlankScene();
    saveInFlight = null;
    saveRequestedWhileRunning = false;
    set({
      project: null,
      scene,
      confirmedScene: cloneScene(scene),
      revision: 1,
      projectId: null,
      mode: "plan",
      tool: "select",
      selectedIds: [],
      selectedId: null,
      saveState: "idle",
      lastSavedAt: null,
      error: null,
      history: [],
      future: [],
      generation: 0,
      draggedCatalogItemId: null,
      cameraByMode: {
        plan: { ...defaultCamera, projection: "orthographic" },
        front: { ...defaultCamera, projection: "orthographic" },
        "3d": structuredClone(defaultCamera),
      },
    });
  },
}));

/**
 * O material da cena que um item da paleta vira.
 *
 * Os dois caminhos — catálogo e material do próprio módulo — descrevem a mesma
 * coisa com campos diferentes, e a pintura por peça não deveria saber qual é
 * qual. A identidade é estável: repintar com o mesmo material não empilha
 * definição nova na cena.
 */
function materialDefinitionOf(material: PaintableMaterial): SceneDocument["materials"][number] {
  if (material.source === "catalog") {
    const item = material.item;
    return {
      id: item.stableItemId,
      revision: item.revision ?? "1.0.0",
      name: item.name,
      category: item.category,
      baseColorFactor: item.pbr?.textureRefs?.baseColor
        ? [1, 1, 1, item.pbr.opacity ?? 1]
        : hexToRgba(item.color, item.pbr?.opacity ?? 1),
      roughness: item.pbr?.roughness ?? 0.65,
      metallic: item.pbr?.metallic ?? 0,
      opacity: item.pbr?.opacity ?? 1,
      textureRefs: item.pbr?.textureRefs ?? {},
      mapping: item.pbr?.mapping ?? {
        mode: "box",
        physicalSizeMm: [1_000, 1_000],
        scale: [1, 1],
        rotationRad: 0,
        offset: [0, 0],
      },
    };
  }
  const imported = material.material;
  return {
    id: `skpmat:${imported.key}`,
    revision: "1.0.0",
    name: imported.name,
    category: "Importado",
    // A cor só tinge quando o SketchUp manda tingir; ver `applyImportedMaterial`.
    baseColorFactor: imported.texture && !imported.colorized
      ? [1, 1, 1, imported.opacity]
      : hexToRgba(imported.colorHex, imported.opacity),
    roughness: 0.8,
    metallic: 0,
    opacity: imported.opacity,
    textureRefs: imported.texture ? { baseColor: imported.texture.assetId } : {},
    mapping: {
      mode: "box",
      physicalSizeMm: imported.texture && imported.texture.widthMm > 0
        ? [imported.texture.widthMm, imported.texture.heightMm || imported.texture.widthMm]
        : [1_000, 1_000],
      scale: [1, 1],
      rotationRad: 0,
      offset: [0, 0],
    },
  };
}

function hexToRgba(value: string, alpha = 1): [number, number, number, number] {
  const color = /^#[0-9A-F]{6}$/i.test(value) ? value.slice(1) : "B9A07C";
  return [
    Number.parseInt(color.slice(0, 2), 16) / 255,
    Number.parseInt(color.slice(2, 4), 16) / 255,
    Number.parseInt(color.slice(4, 6), 16) / 255,
    Math.min(1, Math.max(0, alpha)),
  ];
}
